import { useEffect, useState, useCallback } from "react"
import { usePropertyContext } from "@/lib/usePropertyContext"
import { useActivationEvent } from "@/lib/useActivationEvent"
import { apiClient } from "@/lib/api"
import { QrCodeSvg, downloadQrSvg } from "@/components/QrCodeSvg"

/* ── Inline SVG icons (no deps) ── */

function IconClipboard() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="5" y="2" width="6" height="3" rx="0.5" />
      <path d="M5 3.5H3.5a1 1 0 0 0-1 1v9a1 1 0 0 0 1 1h9a1 1 0 0 0 1-1v-9a1 1 0 0 0-1-1H11" />
    </svg>
  )
}

function IconExternalLink() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6.5 3.5H3.5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-3" />
      <path d="M9.5 2.5h4v4" />
      <path d="M13.5 2.5 7.5 8.5" />
    </svg>
  )
}

function IconDownload() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 2v8.5" />
      <path d="M5 8l3 3 3-3" />
      <path d="M2.5 12.5v1a1 1 0 0 0 1 1h9a1 1 0 0 0 1-1v-1" />
    </svg>
  )
}

function IconInfo() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="7" cy="7" r="6" />
      <path d="M7 6.5v4" />
      <circle cx="7" cy="4.5" r="0.01" fill="currentColor" strokeWidth="2" />
    </svg>
  )
}

/* ── Component ── */

export function GuidebookPreviewPage() {
  const { propertyId } = usePropertyContext()
  useActivationEvent(propertyId, "guidebook_viewed")
  const [renderKey, setRenderKey] = useState<string | null>(null)
  const [propertySlug, setPropertySlug] = useState<string>("")
  const [loading, setLoading] = useState(true)
  const [copiedGuest, setCopiedGuest] = useState(false)
  const [copiedDirect, setCopiedDirect] = useState(false)
  const [copiedQr, setCopiedQr] = useState(false)
  const [previewMode, setPreviewMode] = useState<"mobile" | "desktop">("mobile")
  const [iframeCacheBust] = useState(() => Date.now())
  const [currentTemplate, setCurrentTemplate] = useState<string>("classic")
  const [showQr, setShowQr] = useState(false)
  const [isPms, setIsPms] = useState(false)
  const templateLabels: Record<string, string> = {
    classic: "Classic",
    concierge: "Concierge",
    reserve: "Reserve",
  }

  // Guidebook PIN state
  const [hasGuidebookPinConfigured, setHasGuidebookPinConfigured] = useState(false)
  const [guidebookPin, setGuidebookPin] = useState("")
  const [guidebookPinDirty, setGuidebookPinDirty] = useState(false)
  const [editingPin, setEditingPin] = useState(false)
  const [pinSaving, setPinSaving] = useState(false)
  const [pinSuccess, setPinSuccess] = useState("")
  const [pinError, setPinError] = useState("")

  useEffect(() => {
    if (!propertyId) return
    apiClient.getPropertySettings(propertyId).then((res) => {
      const data = res as {
        ok?: boolean
        settings?: {
          render_key?: string
          property?: { render_key?: string; slug?: string; [k: string]: unknown }
          property_config?: { guidebook_pin_configured?: boolean; [k: string]: unknown }
          integration?: { pms_property_ref?: string; [k: string]: unknown }
          [k: string]: unknown
        }
      }
      const rk = data.settings?.render_key || data.settings?.property?.render_key || ""
      const slug = String(data.settings?.property?.slug || "").trim().toLowerCase()
      const pinConfigured = Boolean(data.settings?.property_config?.guidebook_pin_configured)
      const tmpl = String(data.settings?.property_config?.guidebook_template || "classic")
      const pmsRef = String(data.settings?.integration?.pms_property_ref || "").trim()
      setRenderKey(rk)
      setPropertySlug(slug)
      setHasGuidebookPinConfigured(pinConfigured)
      setCurrentTemplate(tmpl)
      setIsPms(Boolean(pmsRef))
      setLoading(false)
    }).catch(() => setLoading(false))
  }, [propertyId])

  const directGuidebookUrl = renderKey
    ? `${window.location.origin}/g/${renderKey}`
    : null
  const guestLoginUrl = propertySlug
    ? `${window.location.origin}/p/${propertySlug}`
    : null

  const copyLink = useCallback((value: string | null, kind: "guest" | "direct" | "qr") => {
    if (!value) return
    navigator.clipboard.writeText(value).then(() => {
      if (kind === "guest") {
        setCopiedGuest(true)
        setTimeout(() => setCopiedGuest(false), 2000)
      } else if (kind === "qr") {
        setCopiedQr(true)
        setTimeout(() => setCopiedQr(false), 2000)
      } else {
        setCopiedDirect(true)
        setTimeout(() => setCopiedDirect(false), 2000)
      }
    }).catch(() => {})
  }, [])

  async function handleSavePin() {
    if (!propertyId) return
    setPinSaving(true)
    setPinError("")
    setPinSuccess("")
    const payload: Record<string, unknown> = {}
    if (guidebookPinDirty && guidebookPin.trim()) {
      payload.guidebook_pin = guidebookPin.trim()
    } else {
      setPinSaving(false)
      return
    }
    try {
      const res = await apiClient.savePropertySettings(payload, propertyId)
      if (res.ok) {
        setGuidebookPin("")
        setGuidebookPinDirty(false)
        setEditingPin(false)
        setHasGuidebookPinConfigured(true)
        setPinSuccess("PIN saved")
        setTimeout(() => setPinSuccess(""), 3000)
      } else {
        setPinError(res.error || "Failed to save PIN")
      }
    } catch {
      setPinError("Failed to save PIN")
    }
    setPinSaving(false)
  }

  async function handleClearPin() {
    if (!propertyId) return
    setPinSaving(true)
    setPinError("")
    setPinSuccess("")
    try {
      const res = await apiClient.savePropertySettings({ clear_guidebook_pin: true }, propertyId)
      if (res.ok) {
        setGuidebookPin("")
        setGuidebookPinDirty(false)
        setEditingPin(false)
        setHasGuidebookPinConfigured(false)
        setPinSuccess("PIN removed")
        setTimeout(() => setPinSuccess(""), 3000)
      } else {
        setPinError(res.error || "Failed to remove PIN")
      }
    } catch {
      setPinError("Failed to remove PIN")
    }
    setPinSaving(false)
  }

  if (loading) {
    return (
      <div>
        <div className="page-header">
          <h1 className="page-header__title">Guidebook</h1>
        </div>
        <div className="sf-spinner" style={{ marginTop: "var(--sf-space-8)", marginBottom: "var(--sf-space-8)" }} />
      </div>
    )
  }

  if (!renderKey) {
    return (
      <div>
        <div className="page-header">
          <h1 className="page-header__title">Guidebook</h1>
          <p className="page-header__subtitle">
            Unable to load guidebook link. The property may not be fully configured.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-header__title">Guidebook</h1>
        <p className="page-header__subtitle">
          Publish and share your guidebook with guests.
        </p>
      </div>

      <div className={`gb-page__layout${previewMode === "desktop" ? " gb-page__layout--desktop-preview" : ""}`}>
        {/* ── Left column: config ── */}
        <div className="gb-page__config">

          {/* Sharing card */}
          <div className="section-card gb-sharing">
            <h2 className="gb-section-title">Sharing</h2>

            <div className="gb-callout">
              <IconInfo />
              <span>Guests log in using the last name in their booking and check-in date.</span>
            </div>

            {/* Guest login link */}
            <div className="gb-share-row">
              <span className="gb-share-row__label">Guest login</span>
              <code className="gb-share-row__url">
                {guestLoginUrl || "Property slug missing."}
              </code>
              <div className="gb-share-row__actions">
                <button
                  type="button"
                  className={`gb-icon-btn${copiedGuest ? " gb-icon-btn--copied" : ""}`}
                  onClick={() => copyLink(guestLoginUrl, "guest")}
                  disabled={!guestLoginUrl}
                  title="Copy link"
                >
                  <IconClipboard />
                  <span className="gb-icon-btn__tooltip">{copiedGuest ? "Copied!" : "Copy"}</span>
                </button>
                {guestLoginUrl && (
                  <a
                    href={guestLoginUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="gb-icon-btn"
                    title="Open in new tab"
                  >
                    <IconExternalLink />
                    <span className="gb-icon-btn__tooltip">Open</span>
                  </a>
                )}
              </div>
            </div>

            {/* Direct host link */}
            <div className="gb-share-row">
              <span className="gb-share-row__label">Host link</span>
              <code className="gb-share-row__url">{directGuidebookUrl}</code>
              <div className="gb-share-row__actions">
                <button
                  type="button"
                  className={`gb-icon-btn${copiedDirect ? " gb-icon-btn--copied" : ""}`}
                  onClick={() => copyLink(directGuidebookUrl, "direct")}
                  title="Copy link"
                >
                  <IconClipboard />
                  <span className="gb-icon-btn__tooltip">{copiedDirect ? "Copied!" : "Copy"}</span>
                </button>
                {directGuidebookUrl && (
                  <a
                    href={directGuidebookUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="gb-icon-btn"
                    title="Open in new tab"
                  >
                    <IconExternalLink />
                    <span className="gb-icon-btn__tooltip">Open</span>
                  </a>
                )}
              </div>
            </div>

            {/* QR code — collapsible */}
            {guestLoginUrl && (
              <div className="gb-share-qr">
                <button
                  type="button"
                  className="gb-share-qr__trigger"
                  onClick={() => setShowQr(!showQr)}
                >
                  <span className={`gb-share-qr__chevron${showQr ? " gb-share-qr__chevron--open" : ""}`}>
                    &#9654;
                  </span>
                  QR code for in-home access
                </button>
                {showQr && (
                  <div className="gb-share-qr__body">
                    <div className="gb-share-qr__preview">
                      <QrCodeSvg value={guestLoginUrl} size={140} />
                    </div>
                    <div className="gb-share-qr__meta">
                      <p className="gb-share-qr__desc">
                        Place this QR code inside your property so guests can open the guidebook instantly.
                      </p>
                      <div className="gb-share-row__actions">
                        <button
                          type="button"
                          className={`gb-icon-btn${copiedQr ? " gb-icon-btn--copied" : ""}`}
                          onClick={() => copyLink(guestLoginUrl, "qr")}
                          title="Copy link"
                        >
                          <IconClipboard />
                          <span className="gb-icon-btn__tooltip">{copiedQr ? "Copied!" : "Copy"}</span>
                        </button>
                        <button
                          type="button"
                          className="gb-icon-btn gb-icon-btn--labeled"
                          onClick={() => downloadQrSvg(guestLoginUrl, propertySlug)}
                          title="Download QR code"
                        >
                          <IconDownload />
                          Download for print
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* PIN card — only for iCal properties */}
          {!isPms && (
            <div className="section-card">
              <h2 className="gb-section-title">Guidebook PIN</h2>
              <p className="gb-pin__desc">
                Unlocks WiFi and stay details for iCal-sourced bookings. Stored hashed, never shown back.
              </p>

              {pinError && (
                <div className="sf-result-banner sf-result-banner--error" style={{ marginBottom: "var(--sf-space-3)" }}>{pinError}</div>
              )}
              {pinSuccess && (
                <div className="sf-result-banner sf-result-banner--success" style={{ marginBottom: "var(--sf-space-3)" }}>{pinSuccess}</div>
              )}

              {/* Configured state — compact row */}
              {hasGuidebookPinConfigured && !editingPin && (
                <div className="gb-pin__configured">
                  <span className="gb-pin__masked">&#8226;&#8226;&#8226;&#8226;&#8226;&#8226;</span>
                  <span className="status-pill status-pill--healthy" style={{ fontSize: "var(--sf-text-xs)" }}>
                    <span className="status-pill__dot" />
                    Configured
                  </span>
                  <button
                    type="button"
                    className="gb-pin__update-link"
                    onClick={() => setEditingPin(true)}
                  >
                    Update
                  </button>
                </div>
              )}

              {/* Edit / create state */}
              {(editingPin || !hasGuidebookPinConfigured) && (
                <div className="gb-pin__edit">
                  <div>
                    <label className="login-card__label" htmlFor="gb-pin">PIN</label>
                    <input
                      id="gb-pin"
                      className="login-card__input"
                      type="password"
                      value={guidebookPin}
                      onChange={(e) => {
                        setGuidebookPin(e.target.value)
                        setGuidebookPinDirty(true)
                      }}
                      placeholder={hasGuidebookPinConfigured ? "Enter new PIN..." : "4-12 digits"}
                      style={{ height: 36, fontSize: "var(--sf-text-sm)", width: "100%" }}
                    />
                    <div className="settings-field-hint">4-12 digits</div>
                  </div>
                  <div className="gb-pin__edit-actions">
                    <button
                      className="sf-btn sf-btn--primary sf-btn--sm"
                      type="button"
                      onClick={handleSavePin}
                      disabled={pinSaving || !guidebookPinDirty}
                      style={{ height: 32 }}
                    >
                      {pinSaving ? "Saving..." : "Save"}
                    </button>
                    {editingPin && hasGuidebookPinConfigured && (
                      <button
                        className="sf-btn sf-btn--ghost sf-btn--sm"
                        type="button"
                        onClick={() => {
                          setEditingPin(false)
                          setGuidebookPin("")
                          setGuidebookPinDirty(false)
                          setPinError("")
                        }}
                        style={{ height: 32 }}
                      >
                        Cancel
                      </button>
                    )}
                    {editingPin && hasGuidebookPinConfigured && (
                      <button
                        className="sf-btn sf-btn--ghost sf-btn--sm"
                        type="button"
                        onClick={handleClearPin}
                        disabled={pinSaving}
                        style={{ height: 32, color: "var(--sf-error)" }}
                      >
                        Remove PIN
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

        </div>

        {/* ── Right column: sticky preview ── */}
        <div className="gb-page__preview">
          <div className="gb-preview-header">
            <span className="gb-template-badge">
              {templateLabels[currentTemplate] || "Classic"}
            </span>
            <div className="gb-preview-toggle">
              <button
                type="button"
                onClick={() => setPreviewMode("mobile")}
                className={`gb-preview-toggle__btn${previewMode === "mobile" ? " gb-preview-toggle__btn--active" : ""}`}
              >
                Mobile
              </button>
              <button
                type="button"
                onClick={() => setPreviewMode("desktop")}
                className={`gb-preview-toggle__btn${previewMode === "desktop" ? " gb-preview-toggle__btn--active" : ""}`}
              >
                Desktop
              </button>
            </div>
          </div>

          <div className="gb-preview-frame">
            <div className={`gb-preview-frame__device gb-preview-frame__device--${previewMode}`}>
              <iframe
                src={`/g/${renderKey}?t=${iframeCacheBust}`}
                title="Guidebook preview"
                className="gb-preview-frame__iframe"
              />
            </div>
          </div>
        </div>

      </div>
    </div>
  )
}
